
export interface LoanProgram {
  id: string;
  name: string;
  baseRate: number;
  downPaymentPercent: number;
  eligibilityScore: number;
  recommended: boolean;
  eligibleForUser: boolean;
  description?: string;
  benefits?: string[];
  vaStatus?: 'activeDuty' | 'separatedRetired';
  vaDisabilityStatus?: 'none' | 'partial' | 'full';
  fundingFeePercent?: number;
}

export interface CalculatedLoanProgram extends LoanProgram {
  monthlyPayment: number;
  totalCost: number;
  downPayment: number;
  downPercent: number;
  apr: number;
}

export const calculateMortgagePayment = (
  loanAmount: number,
  annualRate: number,
  termYears: number
): number => {
  const monthlyRate = annualRate / 100 / 12;
  const numberOfPayments = termYears * 12;
  
  if (monthlyRate === 0) {
    return loanAmount / numberOfPayments;
  }
  
  const monthlyPayment = loanAmount * 
    (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) /
    (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
    
  return monthlyPayment;
};

export const calculateLoanProgramValues = (
  program: LoanProgram,
  termYears: number,
  mortgageData: any
): CalculatedLoanProgram => {
  const downPayment = mortgageData.homePrice * (program.downPaymentPercent / 100);
  const loanAmount = mortgageData.homePrice - downPayment;
  
  // Calculate monthly payment
  const monthlyPayment = calculateMortgagePayment(loanAmount, program.baseRate, termYears);
  
  // Calculate total cost over the life of the loan
  const totalInterest = (monthlyPayment * termYears * 12) - loanAmount;
  const totalCost = loanAmount + totalInterest + downPayment;
  
  // Calculate APR (simplified - in reality would include all fees)
  const apr = program.baseRate; // Simplified for now
  
  return {
    ...program,
    monthlyPayment: Math.round(monthlyPayment),
    totalCost: Math.round(totalCost),
    downPayment: Math.round(downPayment),
    downPercent: program.downPaymentPercent,
    apr: apr
  };
};
